import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Folder, FolderRoleEnum, FolderShareFragment, ShareFolderInput } from '@app/models';
import { ErrorHandlerService, FolderService, NotyService, OrganizationService } from '@app/services';
import { LoaderService, SelectOption } from '@app/shared';
import { EmailInputComponent } from '@app/shared/email-input/email-input.component';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { delay, EMPTY, finalize, map, Observable } from 'rxjs';

export type ModalResult = FolderShareFragment[];

export interface ModalPublicProperties {
  folder: Folder;
}

@Component({
  selector: 'app-add-folder-sharing-modal',
  templateUrl: './add-folder-sharing-modal.component.html',
  styleUrl: './add-folder-sharing-modal.component.scss',
  standalone: false
})
export class AddFolderSharingModalComponent implements AfterViewInit {
  @Input() isGroup = false;
  @Input() folder: Folder;
  @Input() organizationId?: number;

  @ViewChild('peopleEmails', { static: false }) private peopleEmails: EmailInputComponent;

  form!: UntypedFormGroup;
  rolesAsOptions = [
    {
      key: FolderRoleEnum.Editor,
      value: this.translateService.instant('documents.shareFolder.editor')
    },
    {
      key: FolderRoleEnum.Viewer,
      value: this.translateService.instant('documents.shareFolder.viewer')
    }
  ];
  groupsAsOptions: SelectOption[] = null;

  readonly FolderRoleEnum = FolderRoleEnum;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private folderService: FolderService,
    private errorHandlerService: ErrorHandlerService,
    private loaderService: LoaderService,
    private organizationService: OrganizationService,
    private notyService: NotyService
  ) {
    this.form = this.formBuilder.group({ shares: this.formBuilder.group({ emails: [], group_id: null, role: FolderRoleEnum.Viewer, message: '' }) });
  }

  ngAfterViewInit(): void {
    if (!this.isGroup) {
      this.peopleEmails.focusInput();
    } else {
      this.loaderService.show();

      this.organizationGroups()
        .pipe(finalize(() => this.loaderService.hide()))
        .subscribe({
          next: groups => (this.groupsAsOptions = groups)
        });
    }
  }

  addSharing() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { group_id, emails, role, message } = this.form.getRawValue().shares;
    let shares: ShareFolderInput[];

    if (this.isGroup) {
      if (!group_id) return;
      shares = [{ role, group_id }];
    } else {
      if (!emails || emails.length === 0) return;
      shares = emails.map((email: string) => ({ role, email }));
    }

    this.loaderService.show();

    this.folderService
      .shareFolder({ folderId: this.folder.id, shares, emailMessage: message })
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: folder => {
          this.notyService.success(this.translateService.instant('documents.shareFolder.sharingAdded'));
          this.modal.close(folder.shares);
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  organizationGroups(): Observable<SelectOption[]> {
    if (!this.organizationId) return EMPTY;

    return this.organizationService.slimOrganizationGroups({ page: 1, limit: 8, organizationId: this.organizationId }).pipe(
      delay(0),
      map(page => (page.data || []).filter(item => !!item)),
      map(groups => groups.map(group => ({ key: group.id, value: group.name })))
    );
  }
}
